import React, { useEffect, useMemo, useRef, useState } from 'react';
import API_URL from '@/config/api';

let staffCache = null;
export async function loadStaffDirectory() {
  if (staffCache) return staffCache;
  const res = await fetch(`${API_URL}/api/support/staff-directory`, { credentials: 'include' });
  staffCache = res.ok ? (await res.json()).staff : [];
  return staffCache;
}

/** Bold any @Name that matches a known staff member (or generic @Word) when rendering a note body. */
export function renderWithMentions(text) {
  if (!text) return null;
  const parts = text.split(/(@[A-Z][\w'-]*(?: [A-Z][\w'-]*)?)/g);
  return parts.map((p, i) => (p.startsWith('@') ? <strong key={i} className="text-[#7A0B7A]">{p}</strong> : p));
}

/**
 * Textarea/input with @mention autocomplete. Reports `{ text, mentionIds }` via onChange.
 * `single` renders a one-line input (board quick note); Enter submits via onSubmit.
 */
export function MentionInput({ value, mentionIds, onChange, onSubmit, placeholder, disabled, single = false, testid = 'mention-input', autoFocus = false, className = '' }) {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const ref = useRef(null);
  useEffect(() => { loadStaffDirectory().then(setStaff); }, []);

  const matches = useMemo(() => (open ? staff.filter((s) => s.name.toLowerCase().includes(query.toLowerCase())).slice(0, 6) : []), [open, query, staff]);

  const handle = (e) => {
    const text = e.target.value;
    const pos = e.target.selectionStart ?? text.length;
    const before = text.slice(0, pos);
    const m = before.match(/(?:^|\s)@([\w'-]*(?: [\w'-]*)?)$/);
    setOpen(!!m); setQuery(m ? m[1] : ''); setCursor(0);
    onChange({ text, mentionIds: (mentionIds || []).filter((id) => { const s = staff.find((x) => x.user_id === id); return !s || text.includes(`@${s.name}`); }) });
  };
  const pick = (s) => {
    const el = ref.current; const pos = el?.selectionStart ?? value.length;
    const before = value.slice(0, pos).replace(/@([\w'-]*(?: [\w'-]*)?)$/, `@${s.name} `);
    const text = before + value.slice(pos);
    onChange({ text, mentionIds: Array.from(new Set([...(mentionIds || []), s.user_id])) });
    setOpen(false);
    setTimeout(() => { el?.focus(); el?.setSelectionRange(before.length, before.length); }, 0);
  };
  const key = (e) => {
    if (open && matches.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => (c + 1) % matches.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => (c - 1 + matches.length) % matches.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(matches[cursor]); return; }
      if (e.key === 'Escape') { setOpen(false); return; }
    }
    if (single && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit?.(); }
  };
  const Tag = single ? 'input' : 'textarea';
  return (
    <div className="relative">
      <Tag ref={ref} value={value} onChange={handle} onKeyDown={key} onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder={placeholder} disabled={disabled} autoFocus={autoFocus}
        rows={single ? undefined : 3} data-testid={testid}
        className={`w-full text-sm rounded-md border border-slate-300 bg-white px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50 ${className}`} />
      {open && matches.length > 0 && (
        <ul className="absolute z-30 left-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-md shadow-lg py-1 text-sm" data-testid={`${testid}-suggestions`}>
          {matches.map((s, i) => (
            <li key={s.user_id}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(s); }} className={`w-full text-left px-3 py-1.5 flex justify-between ${i === cursor ? 'bg-slate-100' : 'hover:bg-slate-50'}`} data-testid={`${testid}-suggestion-${s.user_id}`}>
                <span className="font-medium text-slate-900">{s.name}</span><span className="text-[10px] uppercase text-slate-400">{s.role}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
